import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor } from 'helpers/utils';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DatasetComponent
]);

const getOption = landCoverChartData => {
  const categories = landCoverChartData.date;
  const seriesData = landCoverChartData.series;

  const formatSeriesName = name => {
    return name.includes('_') ? name.replace('_', ' ') : name;
  };

  const series = seriesData.map(seriesItem => {
    const formattedName = formatSeriesName(seriesItem.name);
    return {
      name: formattedName,
      type: formattedName === 'Temperature' ? 'line' : 'bar',
      data: seriesItem.data,
      yAxisIndex: formattedName === 'Temperature' ? 1 : 0,
      barMaxWidth: 100,
      lineStyle:
        formattedName === 'Temperature'
          ? { color: getColor('warning') }
          : undefined,
      itemStyle:
        formattedName === 'Temperature'
          ? {
              color: getColor('warning'),
              borderColor: getColor('warning'),
              borderWidth: 2
            }
          : { borderRadius: [3, 3, 0, 0] },
      symbol: formattedName === 'Temperature' ? 'circle' : undefined,
      symbolSize: formattedName === 'Temperature' ? 10 : undefined
    };
  });

  return {
    color: [getColor('primary'), getColor('success'), getColor('warning')],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      transitionDuration: 0,
      formatter: params => {
        const xAxisData = params[0].axisValueLabel;
        const content = params
          .map(param => {
            const unit = param.seriesName === 'Temperature' ? '°C' : '%';
            return `
              <div style="display: flex; justify-content: space-between;">
                <span style="padding-right: 10px">${param.marker} ${
              param.seriesName
            }${':'}</span>
                <span><b>${param.value.toFixed(3)} ${unit}</b></span>
              </div>`;
          })
          .join('');

        return `
          <div>
            <div style="margin-bottom: 5px;">${xAxisData}</div>
            ${content}
          </div>`;
      }
    },
    legend: {
      data: seriesData.map(item => formatSeriesName(item.name)),
      left: 'center',
      top: 'top',
      icon: 'circle',
      inactiveColor: getColor('gray-400'),
      textStyle: { color: getColor('gray-700') }
    },
    yAxis: [
      {
        type: 'value',
        name: 'SoC (%)',
        // min: 0,
        // max: 1,
        // interval: 0.2,
        axisLabel: {
          formatter: '{value}',
          color: getColor('gray-500')
        },
        axisLine: {
          lineStyle: {
            color: getColor('gray-500'),
            type: 'solid'
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: getColor('gray-200')
          }
        }
      },
      {
        type: 'value',
        name: 'Temperature (°C)',
        // min: 0,
        // max: 80,
        // interval: 10,
        axisLabel: {
          formatter: '{value}',
          color: getColor('gray-500')
        },
        axisLine: {
          lineStyle: {
            color: getColor('gray-500'),
            type: 'solid'
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: getColor('gray-200')
          }
        }
      }
    ],
    xAxis: {
      type: 'category',
      axisLine: {
        show: true,
        lineStyle: { color: getColor('gray-300'), type: 'solid' }
      },
      axisLabel: {
        color: getColor('gray-500'),
        // rotate: 45,
        interval: 0
      },
      axisTick: { show: true },
      splitLine: { show: false },
      data: categories
    },
    series: series,
    grid: { right: 60, left: 60, bottom: '15%', top: '15%' }
  };
};

const ChartBody = ({ landCoverChartData }) => {
  const [chartOption, setChartOption] = useState({});

  useEffect(() => {
    if (landCoverChartData) {
      setChartOption(getOption(landCoverChartData));
    }
  }, [landCoverChartData]);

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={chartOption}
      style={{ minHeight: '25.75rem' }}
    />
  );
};

ChartBody.propTypes = {
  landCoverChartData: PropTypes.shape({
    date: PropTypes.arrayOf(PropTypes.string),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number)
      })
    )
  }).isRequired
};

export default ChartBody;
